import React, { createContext, useState, type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router';

export interface OffCanvasMenuProps {
    children: ReactNode;
    /** Specify the side from which the menu appears. Defaults to "left". */
    align?: 'left' | 'right';
}

interface OffCanvasMenuContextProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    align: 'left' | 'right';
}

export const OffCanvasMenuContext = createContext<OffCanvasMenuContextProps | undefined>(undefined);

/**
 * The root OffCanvasMenu component that provides state and configuration to its children.
 */
export const OffCanvasMenu: React.FC<OffCanvasMenuProps> = props => {
    const { children, align = 'left' } = props;
    const [open, setOpen] = useState(false);
    const location = useLocation(); // Get the current location

    // Lock the body scroll when the menu is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    // Close the menu whenever the route changes
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const contextValue = React.useMemo(() => ({ open, setOpen, align }), [open, setOpen, align]);

    return <OffCanvasMenuContext.Provider value={contextValue}>{children}</OffCanvasMenuContext.Provider>;
};
